import React from 'react';
import './LegalPages.css';

function PrivacyPolicyPage() {
  return (
    <div className="legal-page">
      <div className="legal-hero">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: February 1, 2026</p>
      </div>

      <div className="legal-content">
        <section className="legal-section">
          <h2>1. Information We Collect</h2>
          <p>Discover Vanuatu ("we," "our," or "us") collects information to provide better services to our users. We may collect the following types of information:</p>
          <ul>
            <li><strong>Personal Information:</strong> Name, email address, phone number, and other contact details you provide when using our services.</li>
            <li><strong>Usage Data:</strong> Information about how you interact with our website and mobile application, including pages visited, search queries, and click patterns.</li>
            <li><strong>Location Data:</strong> With your permission, we may collect location information to provide location-based services.</li>
            <li><strong>Device Information:</strong> Browser type, device type, operating system, IP address, and unique device identifiers.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>2. How We Use Your Information</h2>
          <p>We use the collected information for the following purposes:</p>
          <ul>
            <li>To provide, maintain, and improve our services</li>
            <li>To personalize your experience and deliver relevant content</li>
            <li>To communicate with you about updates, promotions, and service announcements</li>
            <li>To analyze usage patterns and optimize our platform</li>
            <li>To detect, prevent, and address technical issues and security threats</li>
            <li>To display targeted advertisements from our business partners</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. Information Sharing and Disclosure</h2>
          <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
          <ul>
            <li><strong>With Business Listings:</strong> When you contact a business through our platform, we may share your contact information with that business.</li>
            <li><strong>With Service Providers:</strong> Third-party companies that help us operate our services, such as hosting providers and analytics services.</li>
            <li><strong>For Legal Purposes:</strong> When required by law, court order, or governmental authority.</li>
            <li><strong>Business Transfers:</strong> In connection with any merger, sale of company assets, or acquisition.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Cookies and Tracking Technologies</h2>
          <p>We use cookies, web beacons, and similar tracking technologies to collect information about your browsing activities. You can control cookies through your browser settings, but disabling cookies may limit certain features of our service.</p>
        </section>

        <section className="legal-section">
          <h2>5. Data Security</h2>
          <p>We implement reasonable security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.</p>
        </section>

        <section className="legal-section">
          <h2>6. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access, update, or delete your personal information</li>
            <li>Opt-out of marketing communications</li>
            <li>Disable location tracking in your device settings</li>
            <li>Request a copy of the data we hold about you</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>7. Third-Party Links</h2>
          <p>Our platform may contain links to third-party websites and services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies.</p>
        </section>

        <section className="legal-section">
          <h2>8. Children's Privacy</h2>
          <p>Our services are not directed to children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected such information, please contact us immediately.</p>
        </section>

        <section className="legal-section disclaimer">
          <h2>9. No Guarantee or Warranty</h2>
          <p><strong>IMPORTANT:</strong> While we strive to protect your information, we provide our services "AS IS" without any warranties or guarantees of any kind, express or implied. We do not guarantee:</p>
          <ul>
            <li>The absolute security of your data</li>
            <li>That our services will be uninterrupted, error-free, or completely secure</li>
            <li>The accuracy, reliability, or completeness of any information on our platform</li>
            <li>That our privacy practices will prevent all unauthorized access or data breaches</li>
          </ul>
          <p>You acknowledge that you use our services at your own risk.</p>
        </section>

        <section className="legal-section disclaimer">
          <h2>10. No Refund Policy</h2>
          <p>All payments made for premium services, advertisements, or featured listings are <strong>non-refundable</strong>. This includes but is not limited to:</p>
          <ul>
            <li>Business listing upgrades</li>
            <li>Featured advertisements and promotions</li>
            <li>Premium subscription fees</li>
            <li>Any other paid services or products</li>
          </ul>
          <p>We do not offer refunds, credits, or prorated charges for partially used services or subscription periods.</p>
        </section>

        <section className="legal-section disclaimer">
          <h2>11. No Obligation Policy</h2>
          <p>Discover Vanuatu operates on a voluntary basis and has <strong>no obligation</strong> to:</p>
          <ul>
            <li>Verify the accuracy of business information or user-submitted content</li>
            <li>Monitor, edit, or remove any content posted by users or businesses</li>
            <li>Maintain or continue offering any specific feature or service</li>
            <li>Provide customer support or respond to inquiries within any specific timeframe</li>
            <li>Resolve disputes between users and businesses listed on our platform</li>
            <li>Guarantee the availability or uptime of our services</li>
            <li>Provide compensation for any losses, damages, or inconveniences arising from your use of our platform</li>
          </ul>
          <p>We reserve the right to modify, suspend, or discontinue any part of our services at any time without notice or liability.</p>
        </section>

        <section className="legal-section">
          <h2>12. Limitation of Liability</h2>
          <p>To the maximum extent permitted by law, Discover Vanuatu and its affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:</p>
          <ul>
            <li>Loss of data or information</li>
            <li>Loss of profits or business opportunities</li>
            <li>Unauthorized access to your information</li>
            <li>Any damages arising from your use of our services</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>13. Changes to This Privacy Policy</h2>
          <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of our services after any changes constitutes acceptance of the updated policy.</p>
        </section>

        <section className="legal-section">
          <h2>14. Contact Us</h2>
          <p>If you have any questions or concerns about this Privacy Policy, please contact us at:</p>
          <p className="contact-info">
            <strong>Email:</strong> privacy@discovervanuatu.com<br/>
            <strong>Address:</strong> Port Vila, Vanuatu
          </p>
        </section>
      </div>
    </div>
  );
}

export default PrivacyPolicyPage;
